(function () {
  "use strict";

  if (customElements.get("you-may-also-like")) return;

  const HIDDEN_CLASS = "is-hidden";
  const HIDDEN_TAB_CLASS = "hidden";
  const CURRENT_CLASS = "current";
  const RELATED_PRODUCTS_TAG = "related-products";
  const RECENTLY_VIEWED_TAG = "recently-viewed";
  const VISIBLE_SLIDE_CLASS = "is-visible";
  const SLIDE_SELECTOR = "[data-grid-item]";
  const SLIDER_SELECTOR = "[data-grid-slider]";
  const PREV_BUTTON_SELECTOR = "[data-button-prev]";
  const NEXT_BUTTON_SELECTOR = "[data-button-next]";
  const CARD_SELECTOR = "[data-yma-card]";
  const AVAILABLE_ATTR = "data-available";
  const LOOP_FLAG = "themeYmaLoopBound";
  const MAX_RV_SOURCES = 5;
  const MAX_PAGE_FETCHES = 20;

  function isAvailableCard(card) {
    return card.getAttribute(AVAILABLE_ATTR) === "true";
  }

  function collectAvailableCards(root) {
    if (!root) return [];
    return Array.from(root.querySelectorAll(CARD_SELECTOR)).filter(isAvailableCard);
  }

  const DISTRIBUTIONS = {
    1: [10],
    2: [5, 5],
    3: [4, 3, 3],
    4: [3, 3, 2, 2],
    5: [2, 2, 2, 2, 2],
  };

  function readRecentlyViewedCookie(isSalesEvent) {
    const name = isSalesEvent ? "recently_viewed_sales_events" : "shopify_recently_viewed";
    const parts = document.cookie ? document.cookie.split("; ") : [];
    const row = parts.find((r) => r.startsWith(name + "="));
    if (!row) return [];
    const value = row.substring(name.length + 1);
    if (!value) return [];
    try {
      return decodeURIComponent(value).split(" ").filter(Boolean);
    } catch (_) {
      return [];
    }
  }

  async function fetchHandleProductType(handle) {
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || "/";
    const url = `${root}products/${encodeURIComponent(handle)}?section_id=api-product-type`;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, "text/html");
      const node = doc.querySelector("[data-handle][data-product-type]");
      if (!node) return null;
      const productType = node.getAttribute("data-product-type");
      if (!productType) return null;
      return { handle, productType };
    } catch (_) {
      return null;
    }
  }

  async function fetchPoolPage(productHandle, page) {
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || "/";
    const url = `${root}products/${encodeURIComponent(productHandle)}?section_id=api-event-collection-page&page=${page}`;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, "text/html");
      const wrapper = doc.querySelector("[data-yma-pool-page]");
      if (!wrapper) return { cards: [], hasNext: false };
      const cards = collectAvailableCards(wrapper);
      const hasNext = wrapper.getAttribute("data-has-next") === "true";
      return { cards, hasNext };
    } catch (_) {
      return null;
    }
  }

  function pickCards(orderedSources, poolCards, max) {
    const distribution = DISTRIBUTIONS[orderedSources.length] || [];

    const cardsByProductType = new Map();
    for (const card of poolCards) {
      const productType = card.getAttribute("data-product-type");
      if (!productType) continue;
      if (!cardsByProductType.has(productType)) cardsByProductType.set(productType, []);
      cardsByProductType.get(productType).push(card);
    }

    const pickedHandles = new Set();
    const picked = [];
    const perSource = orderedSources.map((_, i) => ({ quotaTarget: distribution[i] || 0, quotaCount: 0 }));

    const tryAdd = (card, sourceIndex, isQuota) => {
      if (picked.length >= max) return false;
      const handle = card.getAttribute("data-handle");
      if (!handle || pickedHandles.has(handle)) return false;
      pickedHandles.add(handle);
      picked.push(card);
      if (isQuota && sourceIndex >= 0 && perSource[sourceIndex]) perSource[sourceIndex].quotaCount++;
      return true;
    };

    for (let i = 0; i < orderedSources.length; i++) {
      const quota = distribution[i] || 0;
      const candidates = cardsByProductType.get(orderedSources[i].productType) || [];
      let added = 0;
      for (const card of candidates) {
        if (added >= quota) break;
        if (tryAdd(card, i, true)) added++;
      }
      if (picked.length >= max) break;
    }

    if (picked.length < max) {
      for (let i = 0; i < orderedSources.length; i++) {
        const candidates = cardsByProductType.get(orderedSources[i].productType) || [];
        for (const card of candidates) {
          if (picked.length >= max) break;
          tryAdd(card, i, false);
        }
        if (picked.length >= max) break;
      }
    }

    return { picked, perSource };
  }

  function quotasSatisfied(perSource) {
    for (const s of perSource) {
      if (s.quotaCount < s.quotaTarget) return false;
    }
    return true;
  }

  function bindInfiniteLoop(gridSliderEl) {
    if (!gridSliderEl || gridSliderEl.dataset[LOOP_FLAG] === "true") return;

    const tryBind = () => {
      const slider = gridSliderEl.querySelector(SLIDER_SELECTOR);
      const prevBtn = gridSliderEl.querySelector(PREV_BUTTON_SELECTOR);
      const nextBtn = gridSliderEl.querySelector(NEXT_BUTTON_SELECTOR);
      if (!slider || !prevBtn || !nextBtn) return false;

      gridSliderEl.dataset[LOOP_FLAG] = "true";

      const stripDisabled = (btn) => {
        if (btn.hasAttribute("disabled")) btn.removeAttribute("disabled");
        if (btn.disabled) btn.disabled = false;
      };
      stripDisabled(prevBtn);
      stripDisabled(nextBtn);

      const disabledObserver = new MutationObserver(() => {
        stripDisabled(prevBtn);
        stripDisabled(nextBtn);
      });
      disabledObserver.observe(prevBtn, { attributes: true, attributeFilter: ["disabled"] });
      disabledObserver.observe(nextBtn, { attributes: true, attributeFilter: ["disabled"] });

      const scrollToSlide = (slide) => {
        if (!slide) return;
        slider.scrollTo({ top: 0, left: slide.offsetLeft, behavior: "smooth" });
      };

      const wrapHandler = (isPrev) => (event) => {
        const slides = slider.querySelectorAll(SLIDE_SELECTOR);
        if (slides.length === 0) return;
        const firstSlide = slides[0];
        const lastSlide = slides[slides.length - 1];
        const atBoundary = isPrev ? firstSlide.classList.contains(VISIBLE_SLIDE_CLASS) : lastSlide.classList.contains(VISIBLE_SLIDE_CLASS);
        if (!atBoundary) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        scrollToSlide(isPrev ? lastSlide : firstSlide);
      };

      prevBtn.addEventListener("click", wrapHandler(true), { capture: true });
      nextBtn.addEventListener("click", wrapHandler(false), { capture: true });

      return true;
    };

    if (tryBind()) return;

    const childObserver = new MutationObserver(() => {
      if (tryBind()) childObserver.disconnect();
    });
    childObserver.observe(gridSliderEl, { childList: true, subtree: true });

    setTimeout(() => childObserver.disconnect(), 5000);
  }

  function bindInfiniteLoopWithin(root) {
    if (!root) return;
    root.querySelectorAll("grid-slider").forEach(bindInfiniteLoop);
  }

  customElements.define(
    "you-may-also-like",
    class YouMayAlsoLike extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.currentProductId = this.dataset.currentProductId;
        this.currentProductHandle = this.dataset.currentProductHandle;
        this.targetId = this.dataset.targetId;
        this.poolId = this.dataset.poolId;
        this.max = parseInt(this.dataset.max, 10) || 10;
        this.isSalesEvent = this.dataset.isSalesEvent === "true";

        this.relatedProducts = this.closest(RELATED_PRODUCTS_TAG);
        this.recentlyViewed = this.relatedProducts ? this.relatedProducts.querySelector(RECENTLY_VIEWED_TAG) : null;

        this.ymaLoaded = false;
        this.rvLoaded = !this.recentlyViewed;
        this.ymaCount = 0;

        this.handleTabChange = this.handleTabChange.bind(this);
        this.maybeResolveVisibility = this.maybeResolveVisibility.bind(this);
      }

      connectedCallback() {
        if (this.recentlyViewed) {
          this.recentlyViewed.addEventListener(
            "theme:recently-viewed:loaded",
            () => {
              this.rvLoaded = true;
              this.maybeResolveVisibility();
            },
            { once: true }
          );
        }

        const tabsComponent = this.closest("tabs-component");
        if (tabsComponent) tabsComponent.addEventListener("theme:tab:change", this.handleTabChange);

        this.populate();
      }

      disconnectedCallback() {
        const tabsComponent = this.closest("tabs-component");
        if (tabsComponent) tabsComponent.removeEventListener("theme:tab:change", this.handleTabChange);
      }

      async populate() {
        const pool = this.querySelector("[data-yma-pool]");
        const target = document.getElementById(this.targetId);
        if (!pool || !target) {
          this.finish(0);
          return;
        }

        const cookieHandles = readRecentlyViewedCookie(this.isSalesEvent).filter((h) => h && h !== this.currentProductHandle);
        if (cookieHandles.length === 0) {
          this.finish(0);
          return;
        }

        const sourceHandles = cookieHandles.slice(0, MAX_RV_SOURCES);
        const results = await Promise.all(sourceHandles.map(fetchHandleProductType));
        const orderedSources = results.filter((r) => r && r.productType);
        if (orderedSources.length === 0) {
          this.finish(0);
          return;
        }

        let poolCards = collectAvailableCards(pool);
        let nextPageToFetch = 2;
        let hasNext = true;
        let extraFetches = 0;
        let result = pickCards(orderedSources, poolCards, this.max);

        while (!quotasSatisfied(result.perSource) && hasNext && extraFetches < MAX_PAGE_FETCHES) {
          const pageResult = await fetchPoolPage(this.currentProductHandle, nextPageToFetch);
          extraFetches++;
          if (!pageResult) break;
          if (pageResult.cards.length === 0) {
            hasNext = false;
            break;
          }
          poolCards = poolCards.concat(pageResult.cards);
          hasNext = pageResult.hasNext;
          nextPageToFetch++;
          result = pickCards(orderedSources, poolCards, this.max);
        }

        if (result.picked.length === 0) {
          this.finish(0);
          return;
        }

        const fragment = document.createDocumentFragment();
        result.picked.forEach((card) => fragment.appendChild(card));
        target.appendChild(fragment);
        target.classList.remove(HIDDEN_TAB_CLASS);

        this.finish(result.picked.length);
      }

      finish(count) {
        this.ymaCount = count;
        this.ymaLoaded = true;

        const pool = this.querySelector("[data-yma-pool]");
        if (pool) pool.remove();

        if (count > 0) {
          this.classList.remove(HIDDEN_CLASS);
          const gridSlider = this.querySelector("grid-slider");
          if (gridSlider) gridSlider.dispatchEvent(new CustomEvent("theme:grid-slider:init", { bubbles: true }));
        }

        this.dispatchEvent(new CustomEvent("theme:you-may-also-like:loaded", { bubbles: true, detail: { count } }));

        this.maybeResolveVisibility();
      }

      maybeResolveVisibility() {
        if (!this.ymaLoaded || !this.rvLoaded) return;

        const ymaCount = this.ymaCount;
        const rvCount = this.recentlyViewed ? this.recentlyViewed.querySelectorAll(".product-item").length : 0;

        if (!this.relatedProducts) return;

        if (ymaCount === 0 && rvCount === 0) {
          this.relatedProducts.classList.add(HIDDEN_CLASS);
          return;
        }

        this.relatedProducts.classList.remove(HIDDEN_CLASS);

        const tabsWrapper = this.relatedProducts.querySelector(".tabs-wrapper");
        const tabsHead = tabsWrapper ? tabsWrapper.querySelector(".tabs__head") : null;
        const ymaTabLink = this.relatedProducts.querySelector(".tab-link__yma");
        const ymaTabContent = this.relatedProducts.querySelector(".tabs__yma");
        const rvTabLink = this.relatedProducts.querySelector(".tab-link__recent");
        const rvTabContent = this.relatedProducts.querySelector(".tabs__recently-viewed");

        if (tabsHead) tabsHead.classList.remove(HIDDEN_CLASS);

        if (ymaTabLink) {
          ymaTabLink.classList.toggle(HIDDEN_CLASS, ymaCount === 0);
          ymaTabLink.classList.toggle(HIDDEN_TAB_CLASS, ymaCount === 0);
        }
        if (ymaTabContent) ymaTabContent.classList.toggle(HIDDEN_TAB_CLASS, ymaCount === 0);
        if (rvTabLink) {
          rvTabLink.classList.toggle(HIDDEN_CLASS, rvCount === 0);
          rvTabLink.classList.toggle(HIDDEN_TAB_CLASS, rvCount === 0);
        }
        if (rvTabContent) rvTabContent.classList.toggle(HIDDEN_TAB_CLASS, rvCount === 0);

        const desiredActive = ymaCount > 0 ? ymaTabContent : rvTabContent;
        const desiredActiveLink = ymaCount > 0 ? ymaTabLink : rvTabLink;

        if (desiredActive && desiredActiveLink) {
          this.relatedProducts.querySelectorAll(".tab-content.current").forEach((el) => el.classList.remove(CURRENT_CLASS));
          this.relatedProducts.querySelectorAll(".tab-link.current").forEach((el) => el.classList.remove(CURRENT_CLASS));

          desiredActive.classList.add(CURRENT_CLASS);
          desiredActiveLink.classList.add(CURRENT_CLASS);

          const activeSlider = desiredActive.querySelector("grid-slider");
          if (activeSlider) activeSlider.dispatchEvent(new CustomEvent("theme:grid-slider:init", { bubbles: true }));
        }

        bindInfiniteLoopWithin(this.relatedProducts);
      }

      handleTabChange(event) {
        const tab = event && event.detail && event.detail.tab;
        if (!tab) return;
        const slider = tab.querySelector("grid-slider");
        if (slider) {
          slider.dispatchEvent(new CustomEvent("theme:grid-slider:init", { bubbles: true }));
          bindInfiniteLoop(slider);
        }
      }
    }
  );
})();
